/** My profile — what a user tells Signals about themselves.
 *
 * The chat reads it at the start of a conversation as context about who is
 * asking: it may change what an answer leads with, never what is counted, and
 * nobody else's answers use it. Lines can be imported from ChatGPT or Claude
 * by copy and paste (backend/chainlit/profile_api.py says why).
 *
 * Socketless (App.tsx SOCKETLESS_ROUTES). Built phone-first.
 */
import { Check, Copy, Loader2, Plus, Sparkles, UserRound, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Header } from '@/components/header';
import {
  AboutLine,
  AboutSource,
  IMPORT_PROMPT,
  Profile as ProfileData,
  getProfile,
  saveProfile,
  splitImport
} from '@/components/profile/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SidebarProvider } from '@/components/ui/sidebar';
import { Textarea } from '@/components/ui/textarea';

const MAX_LINES = 25;
const SOURCE_LABEL: Record<AboutSource, string> = {
  manual: 'added by you',
  chatgpt: 'from ChatGPT',
  claude: 'from Claude'
};

type Fields = Omit<ProfileData, 'updated_at' | 'about'>;
const EMPTY: Fields = { display_name: '', job_title: '', focus: '', answer_style: '' };

export default function Profile() {
  const [fields, setFields] = useState<Fields>(EMPTY);
  const [about, setAbout] = useState<AboutLine[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'err'; text: string } | null>(
    null
  );

  useEffect(() => {
    getProfile()
      .then((p) => {
        setFields({
          display_name: p.display_name ?? '',
          job_title: p.job_title ?? '',
          focus: p.focus ?? '',
          answer_style: p.answer_style ?? ''
        });
        setAbout(p.about);
      })
      .catch((e) => setNotice({ kind: 'err', text: e.message }))
      .finally(() => setLoaded(true));
  }, []);

  const save = async (lines: AboutLine[] = about) => {
    setSaving(true);
    setNotice(null);
    try {
      const p = await saveProfile({ ...fields, about: lines });
      setAbout(p.about);
      setDirty(false);
      setNotice({ kind: 'ok', text: 'Saved. It applies from your next chat.' });
    } catch (e: any) {
      setNotice({ kind: 'err', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const field = (key: keyof Fields, value: string) => {
    setFields((f) => ({ ...f, [key]: value }));
    setDirty(true);
  };

  const addNote = () => {
    const text = note.trim();
    if (!text) return;
    setAbout((a) => [...a, { text, source: 'manual' }]);
    setNote('');
    setDirty(true);
  };

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full flex-col">
        <Header />
        <div className="flex-1 overflow-auto">
          <div className="mx-auto max-w-2xl px-4 pb-28 pt-5 sm:px-6 sm:pt-8">
            <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
              <UserRound className="size-3.5" /> My profile
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">About you</h1>
            <p className="mt-2 text-[15px] text-muted-foreground">
              Signals uses this to decide what to lead with and which words to
              use. It never changes what is counted, and nobody else's answers
              use it.
            </p>

            {notice && (
              <div
                className={`mt-4 rounded-lg border px-3 py-2 text-sm ${
                  notice.kind === 'ok'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
                    : 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200'
                }`}
              >
                {notice.text}
              </div>
            )}

            {!loaded ? (
              <div className="grid place-items-center py-16">
                <Loader2 className="size-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <section className="mt-5 grid gap-4 rounded-2xl border bg-card p-4 sm:p-5">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="grid gap-1.5">
                      <Label htmlFor="pf-name">Name</Label>
                      <Input
                        id="pf-name"
                        maxLength={80}
                        value={fields.display_name ?? ''}
                        onChange={(e) => field('display_name', e.target.value)}
                      />
                    </div>
                    <div className="grid gap-1.5">
                      <Label htmlFor="pf-title">Job title</Label>
                      <Input
                        id="pf-title"
                        maxLength={80}
                        placeholder="Finance manager"
                        value={fields.job_title ?? ''}
                        onChange={(e) => field('job_title', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="pf-focus">What you look after</Label>
                    <Textarea
                      id="pf-focus"
                      maxLength={300}
                      className="min-h-[64px]"
                      placeholder="The Alexandria and Delta branches, the pharma product line"
                      value={fields.focus ?? ''}
                      onChange={(e) => field('focus', e.target.value)}
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="pf-style">How you like answers</Label>
                    <Textarea
                      id="pf-style"
                      maxLength={300}
                      className="min-h-[64px]"
                      placeholder="Short, with the table first and amounts in thousands"
                      value={fields.answer_style ?? ''}
                      onChange={(e) => field('answer_style', e.target.value)}
                    />
                  </div>
                </section>

                <section className="mt-6">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <h2 className="text-sm font-semibold">
                      Notes about you
                      <span className="text-muted-foreground">
                        {' '}
                        · {about.length}/{MAX_LINES}
                      </span>
                    </h2>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      onClick={() => setImporting(true)}
                    >
                      <Sparkles className="size-4" /> Import from ChatGPT or Claude
                    </Button>
                  </div>
                  <div className="flex flex-col gap-2">
                    {about.map((a, i) => (
                      <div
                        key={`${a.text}-${i}`}
                        className="flex items-start justify-between gap-2 rounded-xl border bg-card px-3 py-2"
                      >
                        <div className="min-w-0">
                          <div className="text-[15px]">{a.text}</div>
                          <div className="text-xs text-muted-foreground">
                            {SOURCE_LABEL[a.source] ?? SOURCE_LABEL.manual}
                          </div>
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="Remove"
                          onClick={() => {
                            setAbout((list) => list.filter((_, j) => j !== i));
                            setDirty(true);
                          }}
                        >
                          <X className="size-4" />
                        </Button>
                      </div>
                    ))}
                    {about.length < MAX_LINES && (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Add a note, e.g. I report to the CEO every month"
                          maxLength={300}
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && addNote()}
                        />
                        <Button variant="outline" size="icon" aria-label="Add" onClick={addNote}>
                          <Plus className="size-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </section>
              </>
            )}
          </div>
        </div>

        {/* Save stays in reach of the thumb on a phone. */}
        {loaded && (
          <div className="border-t bg-background/95 px-4 py-3 backdrop-blur">
            <div className="mx-auto flex max-w-2xl items-center justify-between gap-3">
              <span className="text-xs text-muted-foreground">
                {dirty ? 'You have unsaved changes.' : 'Applies from your next chat.'}
              </span>
              <Button disabled={!dirty || saving} onClick={() => save()}>
                {saving && <Loader2 className="mr-1 size-4 animate-spin" />}
                Save
              </Button>
            </div>
          </div>
        )}
      </div>

      <ImportDialog
        open={importing}
        room={MAX_LINES - about.length}
        onClose={() => setImporting(false)}
        onAdd={(lines) => {
          const next = [...about, ...lines];
          setAbout(next);
          setImporting(false);
          save(next);
        }}
      />
    </SidebarProvider>
  );
}

function ImportDialog({
  open,
  room,
  onClose,
  onAdd
}: {
  open: boolean;
  room: number;
  onClose: () => void;
  onAdd: (lines: AboutLine[]) => void;
}) {
  const [source, setSource] = useState<Exclude<AboutSource, 'manual'>>('chatgpt');
  const [copied, setCopied] = useState(false);
  const [pasted, setPasted] = useState('');
  const [lines, setLines] = useState<{ text: string; keep: boolean }[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCopied(false);
    setPasted('');
    setLines(null);
    setError(null);
  }, [open]);

  const app = source === 'chatgpt' ? 'ChatGPT' : 'Claude';
  const kept = (lines ?? []).filter((l) => l.keep);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import from ChatGPT or Claude</DialogTitle>
          <DialogDescription>
            Nothing is read from your account there. You copy a question, paste
            its answer here, and keep the lines you choose.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          {(['chatgpt', 'claude'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSource(s)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium ${
                source === s ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground'
              }`}
            >
              {s === 'chatgpt' ? 'ChatGPT' : 'Claude'}
            </button>
          ))}
        </div>

        <div className="grid gap-2">
          <div className="text-sm font-medium">1. Ask {app}</div>
          <div className="rounded-lg border bg-muted/40 p-3 text-sm">{IMPORT_PROMPT}</div>
          <Button
            variant="outline"
            size="sm"
            className="w-fit gap-1.5"
            onClick={() =>
              navigator.clipboard
                .writeText(IMPORT_PROMPT)
                .then(() => setCopied(true))
                .catch(() => setCopied(false))
            }
          >
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            {copied ? 'Copied' : 'Copy the question'}
          </Button>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="pf-paste" className="text-sm font-medium">
            2. Paste {app}'s answer
          </Label>
          <Textarea
            id="pf-paste"
            className="min-h-[120px]"
            value={pasted}
            onChange={(e) => {
              setPasted(e.target.value);
              setLines(null);
            }}
          />
          {lines === null && (
            <Button
              size="sm"
              className="w-fit"
              disabled={!pasted.trim() || busy}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  const r = await splitImport(pasted, source);
                  setLines(r.lines.map((l, i) => ({ text: l.text, keep: i < room })));
                } catch (e: any) {
                  setError(e.message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy && <Loader2 className="mr-1 size-4 animate-spin" />}
              Find the lines
            </Button>
          )}
        </div>

        {lines !== null && (
          <div className="grid gap-2">
            <div className="text-sm font-medium">3. Keep what is about your work</div>
            {lines.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                No lines found. Paste the list part of the answer.
              </div>
            ) : (
              lines.map((l, i) => (
                <label key={i} className="flex cursor-pointer items-start gap-2 text-sm">
                  <Checkbox
                    className="mt-0.5"
                    checked={l.keep}
                    onCheckedChange={(v) =>
                      setLines((all) =>
                        (all ?? []).map((x, j) => (j === i ? { ...x, keep: !!v } : x))
                      )
                    }
                  />
                  <span>{l.text}</span>
                </label>
              ))
            )}
            <p className="text-xs text-muted-foreground">
              Lines that looked like a password, an email address or a phone
              number were left out.
              {kept.length > room ? ` You have room for ${room} more.` : ''}
            </p>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!kept.length || kept.length > room}
            onClick={() => onAdd(kept.map((l) => ({ text: l.text, source })))}
          >
            Add {kept.length || ''} line{kept.length === 1 ? '' : 's'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
